// Pictures, videos, sounds, stickers and files in messages (13-messages.md, "Media"): from the
// authenticated media (the service worker adds the token), decrypted in the browser when encrypted
// (AES-CTR, SHA-256 checked), kept to safe types, never an endless spinner (25 s, then Retry).
// Sounds use the browser's own player and spoilers on pictures aren't drawn (candidates to drop).
// A box has its final size from the first frame; its picture fades in over the blurhash or the
// sender's tint (v1.58.26, part A of Polish 2).
import { decode } from 'blurhash';
import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useMemo, useState, type CSSProperties, type ImgHTMLAttributes } from 'react';
import { Button, IconButton } from '../blocks/Button';
import { Icon } from '../blocks/icons';
import { Spinner, SLOW_MS } from '../blocks/Wait';
import { formatDuration, formatSize, idColour } from '../core/format';
import { setting } from '../core/settings';
import { t } from '../i18n';
import { chatIcons } from './icons';
import { ImageViewer, saveAs, TextViewer } from './viewers';

interface EncryptedFile {
  url: string;
  key: JsonWebKey;
  iv: string;
  hashes: { sha256: string };
}

export interface FileContent {
  body?: string;
  filename?: string;
  msgtype?: string;
  url?: string;
  file?: EncryptedFile;
  info?: { mimetype?: string; size?: number; w?: number; h?: number; duration?: number; thumbnail_url?: string; thumbnail_file?: EncryptedFile; thumbnail_info?: { mimetype?: string }; 'xyz.amorgan.blurhash'?: string };
}

const SAFE = new Set(
  'image/jpeg image/gif image/png image/apng image/webp image/avif video/mp4 video/webm video/ogg audio/mp4 audio/webm audio/aac audio/mpeg audio/ogg audio/wave audio/wav audio/x-wav audio/x-pn-wav audio/flac audio/x-flac application/pdf application/json application/x-sh application/ecmascript application/javascript application/xhtml+xml application/xml text/plain text/html text/css text/javascript text/x-c text/csv text/tab-separated-values text/yaml text/markdown'.split(
    ' ',
  ),
);

/** The type a downloaded file keeps: a safe one, else octet-stream (13, Media). */
export function safeType(type = ''): string {
  const plain = type.split(';')[0].trim().toLowerCase();
  if (plain === 'video/quicktime') return 'video/mp4';
  if (plain === 'application/ogg') return 'audio/ogg';
  return SAFE.has(plain) ? plain : 'application/octet-stream';
}

const bytes = (b64: string) => Uint8Array.from(atob(b64.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(b64.length / 4) * 4, '=')), (c) => c.charCodeAt(0));
const unpadded = (buf: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/=+$/, '');

/** Decrypts an attachment as the Matrix specification describes. */
export async function decryptFile(data: ArrayBuffer, file: EncryptedFile): Promise<ArrayBuffer> {
  if (unpadded(await crypto.subtle.digest('SHA-256', data)) !== file.hashes.sha256.replace(/=+$/, '')) throw new Error('hash');
  const key = await crypto.subtle.importKey('jwk', { ...file.key, alg: 'A256CTR', key_ops: ['decrypt'] }, { name: 'AES-CTR' }, false, ['decrypt']);
  return crypto.subtle.decrypt({ name: 'AES-CTR', counter: bytes(file.iv), length: 64 }, key, data);
}

export const httpOf = (client: MatrixClient, mxc: string) => client.mxcUrlToHttp(mxc, undefined, undefined, undefined, false, true, true) ?? '';

/** Downloads (and decrypts) a file into an object address. */
export async function fetchFile(client: MatrixClient, url: string | undefined, file: EncryptedFile | undefined, type?: string): Promise<string> {
  const mxc = file?.url ?? url;
  if (!mxc) throw new Error('no file');
  const answer = await fetch(httpOf(client, mxc));
  if (!answer.ok) throw new Error(String(answer.status));
  let data = await answer.arrayBuffer();
  if (file) data = await decryptFile(data, file);
  return URL.createObjectURL(new Blob([data], { type: safeType(type) }));
}

type State = { url?: string; failed?: boolean };

/** A file loaded on demand (or at once), with the 25 s limit and Retry. */
export function useFile(client: MatrixClient, content: FileContent, start: boolean, thumb = false) {
  const [state, setState] = useState<State>({});
  const [attempt, setAttempt] = useState(0);
  const [go, setGo] = useState(start);
  const url = thumb ? content.info?.thumbnail_url : content.url;
  const file = thumb ? content.info?.thumbnail_file : content.file;
  const type = thumb ? content.info?.thumbnail_info?.mimetype : content.info?.mimetype;
  useEffect(() => {
    if (!go || (!url && !file)) return;
    let live = true;
    let made: string | undefined;
    const timer = setTimeout(() => live && setState({ failed: true }), SLOW_MS);
    setState({});
    fetchFile(client, url, file, type).then(
      (u) => {
        made = u;
        clearTimeout(timer);
        if (live) setState({ url: u });
      },
      () => live && (clearTimeout(timer), setState({ failed: true })),
    );
    return () => {
      live = false;
      clearTimeout(timer);
      if (made) URL.revokeObjectURL(made);
    };
  }, [client, url, file, type, go, attempt]);
  return { ...state, started: go, start: () => setGo(true), retry: () => setAttempt((a) => a + 1) };
}

/** The picture box's size: 400 px wide at most, the picture's own ratio, 48–600 px high (13, Pictures). */
function boxSize(info: FileContent['info'], width: number) {
  const w = info?.w;
  const h = info?.h;
  if (w && h && w < width) return { width: Math.max(w, 48), height: Math.max(h, 48) };
  return { width, height: Math.min(600, Math.max(48, w && h ? Math.round((h / w) * width) : width)) };
}

function Blurhash({ hash }: { hash?: string }) {
  const src = useMemo(() => {
    if (!hash) return undefined;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 32;
      const ctx = canvas.getContext('2d')!;
      const image = ctx.createImageData(32, 32);
      image.data.set(decode(hash, 32, 32));
      ctx.putImageData(image, 0, 0);
      return canvas.toDataURL();
    } catch {
      return undefined;
    }
  }, [hash]);
  return src ? <img className="blurhash" src={src} alt="" /> : null;
}

/** The sender's ID colour, for the tinted placeholder (13, Pictures). */
export const tint = (sender = '') => ({ '--tint': `var(--name-${idColour(sender)})` }) as CSSProperties;

/** A picture that fades in over what is under it once it has loaded (at once with reduced motion). */
export function FadeIn({ onLoad, ...img }: ImgHTMLAttributes<HTMLImageElement>) {
  const [shown, setShown] = useState(false);
  return <img {...img} className={shown ? 'fade-in' : 'unseen'} onLoad={(e) => (setShown(true), onLoad?.(e))} />;
}

const Badge = ({ children, end }: { children: string; end?: boolean }) => <span className={end ? 'media-badge end' : 'media-badge'}>{children}</span>;

/** A picture or a sticker. */
export function Picture({ client, content, sender, sticker }: { client: MatrixClient; content: FileContent; sender?: string; sticker?: boolean }) {
  const plain = !content.file;
  const auto = setting('auto-load-media') || sticker;
  // An unencrypted picture comes straight from its download address.
  const [show, setShow] = useState(!!auto);
  const [direct, setDirect] = useState<State>({});
  const [attempt, setAttempt] = useState(0);
  const loaded = useFile(client, content, !plain && !!auto);
  const size = sticker ? { width: 76, height: Math.max(24, Math.round(76 * ((content.info?.h ?? 1) / (content.info?.w ?? 1)))) } : boxSize(content.info, 400);
  useEffect(() => {
    if (!plain || !show) return;
    const timer = setTimeout(() => setDirect((d) => (d.url ? d : { failed: true })), SLOW_MS);
    return () => clearTimeout(timer);
  }, [plain, show, attempt]);
  const src = plain ? (show ? `${httpOf(client, content.url ?? '')}${attempt ? `#${attempt}` : ''}` : undefined) : loaded.url;
  const failed = direct.failed || (!plain && loaded.failed);
  const done = !!src && direct.url === src;
  const retry = () => (setDirect({}), plain ? setAttempt((a) => a + 1) : loaded.retry());
  const [viewing, setViewing] = useState(false);
  return (
    <div className={`picture${sticker ? ' sticker' : ''}${done ? ' done' : ''}`} style={{ width: size.width, height: size.height, maxWidth: '100%', ...tint(sender) }}>
      {!sticker && <Blurhash hash={content.info?.['xyz.amorgan.blurhash']} />}
      {src && !failed && <FadeIn src={src} alt={content.body ?? ''} onLoad={() => setDirect({ url: src })} onError={() => setDirect({ failed: true })} />}
      {done && src && <button type="button" className="picture-open" aria-label={content.body ?? ''} onClick={() => setViewing(true)} />}
      {viewing && src && <ImageViewer src={src} name={content.body ?? ''} onClose={() => setViewing(false)} />}
      {!show && !loaded.started && (
        <Button small className="media-middle" onClick={() => (plain ? setShow(true) : loaded.start())}>
          <Icon svg={chatIcons.image} size={18} /> {t('chat.view')}
        </Button>
      )}
      {failed && (
        <Button small variant="critical" className="media-middle" title={t('chat.pictureFailed')} onClick={retry}>
          <Icon svg={chatIcons.warning} size={18} /> {t('common.retry')}
        </Button>
      )}
      {!done && content.info?.size ? <Badge end>{formatSize(content.info.size)}</Badge> : null}
    </div>
  );
}

function FileHeader({ content, onDownload }: { content: FileContent; onDownload?: () => void }) {
  const type = (content.info?.mimetype ?? '').split('/')[1]?.split(';')[0] ?? '';
  return (
    <div className="file-head">
      {type && <span className="file-type">{type}</span>}
      <span className="file-name">{content.filename ?? content.body ?? t('chat.unnamedFile')}</span>
      {onDownload && <IconButton label={t('chat.download', { size: formatSize(content.info?.size ?? 0) })} icon={chatIcons.download} size={18} onClick={onDownload} />}
    </div>
  );
}

/** A video: never loads by itself; Watch downloads it and plays it with the browser's controls. */
export function Video({ client, content, sender }: { client: MatrixClient; content: FileContent; sender?: string }) {
  const video = useFile(client, content, false);
  const thumb = useFile(client, content, !!setting('auto-load-media') && !!(content.info?.thumbnail_url || content.info?.thumbnail_file), true);
  const size = boxSize(content.info, 400);
  const name = content.filename ?? content.body ?? 'video';
  return (
    <div className="attachment">
      <FileHeader content={content} onDownload={() => (video.url ? saveAs(video.url, name) : video.start())} />
      <div className="picture" style={{ width: size.width, height: size.height, maxWidth: '100%', ...tint(sender) }}>
        {video.url ? (
          <video src={video.url} controls autoPlay />
        ) : (
          <>
            <Blurhash hash={content.info?.['xyz.amorgan.blurhash']} />
            {thumb.url && <FadeIn src={thumb.url} alt="" />}
            {!video.started && (
              <Button small className="media-middle" onClick={video.start}>
                <Icon svg={chatIcons.play} size={18} /> {t('chat.watch')}
              </Button>
            )}
            {video.started && !video.failed && <span className="media-middle"><Spinner size={24} /></span>}
            {video.failed && (
              <Button small variant="critical" className="media-middle" title={t('chat.videoFailed')} onClick={video.retry}>
                <Icon svg={chatIcons.warning} size={18} /> {t('common.retry')}
              </Button>
            )}
            {content.info?.duration ? <Badge>{formatDuration(Math.round(content.info.duration / 1000))}</Badge> : null}
            {content.info?.size ? <Badge end>{formatSize(content.info.size)}</Badge> : null}
          </>
        )}
      </div>
    </div>
  );
}

/** A sound: the browser's own player once Play has loaded it. */
export function Sound({ client, content }: { client: MatrixClient; content: FileContent }) {
  const sound = useFile(client, content, false);
  return (
    <div className="attachment">
      <FileHeader content={content} />
      {sound.url ? (
        <audio src={sound.url} controls autoPlay />
      ) : (
        <Button small busy={sound.started && !sound.failed} variant={sound.failed ? 'critical' : undefined} onClick={sound.failed ? sound.retry : sound.start}>
          <Icon svg={sound.failed ? chatIcons.warning : chatIcons.play} size={18} /> {t(sound.failed ? 'common.retry' : 'chat.play')}
        </Button>
      )}
    </div>
  );
}

const TEXT_ENDINGS = new Set('go rs py swift c cpp java kt lua php ts js jsx tsx html xhtml xht css scss sass json md yaml xml txt text conf cfg cnf log sql'.split(' '));
/** A text file opens in the text viewer (13, Files: the safe text types, or a name ending in one of these). */
const isText = (content: FileContent) => {
  const type = safeType(content.info?.mimetype);
  return (type.startsWith('text/') || /json|x-sh|javascript|ecmascript|xml/.test(type)) || TEXT_ENDINGS.has((content.filename ?? content.body ?? '').split('.').pop()?.toLowerCase() ?? '');
};

/** A file: Open file (text), Open PDF and Download, each loading the file first (13, Files). */
export function FileCard({ client, content }: { client: MatrixClient; content: FileContent }) {
  const [want, setWant] = useState<'pdf' | 'save' | 'text'>();
  const [text, setText] = useState<string>();
  const file = useFile(client, content, false);
  const name = content.filename ?? content.body ?? t('chat.unnamedFile');
  const pdf = safeType(content.info?.mimetype) === 'application/pdf';
  const size = formatSize(content.info?.size ?? 0);
  useEffect(() => {
    if (!file.url || !want) return;
    if (want === 'text') void fetch(file.url).then((r) => r.text()).then(setText);
    // The browser's own PDF viewer in a new tab; a blocked tab saves the file instead (14).
    else if (want === 'pdf') {
      if (!open(file.url, '_blank')) saveAs(file.url, name);
    } else saveAs(file.url, name);
    setWant(undefined);
  }, [file.url, want, name]);
  const press = (what: 'pdf' | 'save' | 'text') => {
    setWant(what);
    if (file.failed) file.retry();
    else file.start();
  };
  const busy = file.started && !file.url && !file.failed;
  return (
    <div className="attachment file">
      <FileHeader content={content} />
      <div className="file-buttons">
        {isText(content) && (
          <Button small busy={busy && want === 'text'} variant={file.failed ? 'critical' : undefined} title={file.failed ? t('chat.fileFailed') : undefined} onClick={() => press('text')}>
            {t('chat.openFile')}
          </Button>
        )}
        {pdf && (
          <Button small busy={busy && want === 'pdf'} variant={file.failed ? 'critical' : undefined} title={file.failed ? t('chat.fileFailed') : undefined} onClick={() => press('pdf')}>
            {t('chat.openPdf')}
          </Button>
        )}
        <Button small busy={busy && want === 'save'} variant={file.failed ? 'critical' : undefined} title={file.failed ? t('chat.fileFailed') : undefined} onClick={() => press('save')}>
          <Icon svg={file.failed ? chatIcons.warning : chatIcons.download} size={18} /> {t(file.failed ? 'chat.retryDownload' : 'chat.download', { size })}
        </Button>
      </div>
      {text !== undefined && <TextViewer text={text} name={name} type={content.info?.mimetype ?? ''} onClose={() => setText(undefined)} />}
    </div>
  );
}
